import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import PhotoGalleryService from "../../../_services/PhotoGalleryService";

interface Props {
  show: boolean;
  photoIds: number[];
  currentAlbumId: number;
  onHide: () => void;
  onDone: () => void;
}

interface Album {
  id: number;
  name: string;
}

const AddToAlbumDialog: React.FC<Props> = ({
  show,
  photoIds,
  currentAlbumId,
  onHide,
  onDone,
}) => {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      loadAlbums();
    }
  }, [show]);

  const loadAlbums = async () => {
    setLoading(true);
    try {
      const res: any = await PhotoGalleryService.getAlbums();
      const allAlbums = (res.data?.data || []).filter(
        (a: any) => a.id !== currentAlbumId,
      );
      setAlbums(allAlbums);
    } catch (err) {
      console.error("Failed to load albums", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedAlbumId) return;
    setSaving(true);
    try {
      await PhotoGalleryService.assignPhotosToAlbum(selectedAlbumId, photoIds);
      onDone();
    } catch (err) {
      console.error("Failed to add photos to album", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Add to Album</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : albums.length === 0 ? (
          <p className="text-muted">No other albums available.</p>
        ) : (
          <Form.Group>
            <Form.Label>Select album</Form.Label>
            <Form.Select
              value={selectedAlbumId || ""}
              onChange={(e) => setSelectedAlbumId(Number(e.target.value) || null)}
            >
              <option value="">Choose an album...</option>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAdd}
          disabled={!selectedAlbumId || saving}
        >
          {saving
            ? "Adding..."
            : `Add ${photoIds.length} Photo(s)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddToAlbumDialog;
